import React, { useMemo, useState } from 'react';
import {
  Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  MenuItem, TextField, Typography,
} from '@mui/material';
import InlineAlert from '../../../shared/components/InlineAlert';
import type {
  AgentSpec, AvailableModel, CreateAgentRequest, InvocationShapeInfo, InvocationShapeType, Prompt,
} from '../../../types/backend';
import { THINKING_EFFORT_TIERS } from '../services/agentSpecService';
import { useCreateAgent } from '../hooks/useAgentLifecycle';
import { modelOptions } from './modelOptions';
import ToolScopePicker from './ToolScopePicker';
import { toolWarningsOf } from '../utils/toolWarnings';

// PS-UC-15 step 2: the id is the agent's identity on the wire.
const AGENT_ID_PATTERN = /^[a-z][a-z0-9_]{2,47}$/;

interface NewAgentDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (spec: AgentSpec) => void;
  shapes: InvocationShapeInfo[];
  models: AvailableModel[];
  shellPrompts: Prompt[];
}

interface Draft {
  agent_id: string;
  display_name: string;
  description: string;
  shape: InvocationShapeType | '';
  system_prompt_ref: string;
  model_id: string;
  max_tokens: string;
  thinking_effort: string;
  context_window_size: string;
  tools: string[];
  callees: string[];
}

const EMPTY: Draft = {
  agent_id: '', display_name: '', description: '', shape: '', system_prompt_ref: '', model_id: '',
  max_tokens: '', thinking_effort: '', context_window_size: '', tools: [], callees: [],
};

const positiveInt = (raw: string) => /^\d+$/.test(raw.trim()) && Number(raw.trim()) > 0;

const validate = (d: Draft): Partial<Record<keyof Draft, string>> => {
  const errors: Partial<Record<keyof Draft, string>> = {};
  if (!AGENT_ID_PATTERN.test(d.agent_id)) {
    errors.agent_id = '3 to 48 lower case letters, digits and _, starting with a letter';
  }
  if (!d.display_name.trim()) errors.display_name = 'Display name is required';
  if (!d.description.trim()) errors.description = 'Description is required';
  if (!d.shape) errors.shape = 'Shape is required';
  if (!d.system_prompt_ref) errors.system_prompt_ref = 'System prompt is required';
  if (!d.model_id) errors.model_id = 'Model is required';
  if (!positiveInt(d.max_tokens)) errors.max_tokens = 'Must be a positive whole number';
  if (d.context_window_size.trim() && !positiveInt(d.context_window_size)) {
    errors.context_window_size = 'Must be a positive whole number';
  }
  return errors;
};

/**
 * Making an agent whole (PS-UC-15). The shapes and the tools offered to each come from the backend, and
 * the backend holds every write-boundary check, so its refusal is shown as it said it.
 */
const NewAgentDialog: React.FC<NewAgentDialogProps> = ({
  open, onClose, onCreated, shapes, models, shellPrompts,
}) => {
  const create = useCreateAgent(open);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [attempted, setAttempted] = useState(false);

  const shapeInfo = shapes.find((s) => s.shape === draft.shape) ?? null;
  // A sub_agent is consulted by another agent and consults none (ADR-0064).
  const takesCallees = draft.shape === 'interactive' || draft.shape === 'batch';
  const errors = useMemo(() => validate(draft), [draft]);
  const shown = attempted ? errors : {};

  const set = <K extends keyof Draft>(field: K, value: Draft[K]) => setDraft((prev) => ({ ...prev, [field]: value }));

  const chooseShape = (shape: InvocationShapeType) => {
    const allowed = new Set((shapes.find((s) => s.shape === shape)?.tools ?? []).map((t) => t.action_id));
    setDraft((prev) => ({ ...prev, shape, tools: prev.tools.filter((t) => allowed.has(t)) }));
  };

  const toggle = (list: 'tools' | 'callees', ids: string[], select: boolean) => setDraft((prev) => ({
    ...prev,
    [list]: select
      ? Array.from(new Set([...prev[list], ...ids]))
      : prev[list].filter((id) => !ids.includes(id)),
  }));

  const submit = async () => {
    setAttempted(true);
    if (Object.keys(errors).length > 0 || !draft.shape) return;
    const request: CreateAgentRequest = {
      agent_id: draft.agent_id,
      display_name: draft.display_name.trim(),
      description: draft.description.trim(),
      shape: draft.shape,
      model_id: draft.model_id,
      max_tokens: Number(draft.max_tokens),
      ...(draft.thinking_effort ? { thinking_effort: draft.thinking_effort } : {}),
      ...(draft.context_window_size.trim() ? { context_window_size: Number(draft.context_window_size) } : {}),
      system_prompt_ref: draft.system_prompt_ref,
      // Trimmed as the shipped agent of the same shape is: only investigation, the interactive
      // one, acts for an operator whose access bounds what it sees. The Tools tab changes it.
      tool_loadout: { tools: draft.tools, blocks: [], avp_visibility_filter: draft.shape === 'interactive' },
      callees: takesCallees ? draft.callees : [],
    };
    try {
      onCreated(await create.create(request));
    } catch {
      // surfaced via create.error
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>New agent</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, pt: 1 }}>
          <TextField
            label="Agent id" value={draft.agent_id} size="small" sx={{ flex: '1 1 220px' }}
            onChange={(e) => set('agent_id', e.target.value)}
            error={!!shown.agent_id}
            helperText={shown.agent_id ?? 'Fixed once created'}
          />
          <TextField
            label="Display name" value={draft.display_name} size="small" sx={{ flex: '1 1 220px' }}
            onChange={(e) => set('display_name', e.target.value)}
            error={!!shown.display_name} helperText={shown.display_name ?? ' '}
          />
          <TextField
            label="Description" value={draft.description} size="small" sx={{ flex: '2 1 360px' }}
            onChange={(e) => set('description', e.target.value)}
            error={!!shown.description} helperText={shown.description ?? ' '}
          />
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
          <TextField
            select label="Shape" value={draft.shape} size="small" sx={{ flex: '1 1 200px' }}
            onChange={(e) => chooseShape(e.target.value as InvocationShapeType)}
            error={!!shown.shape} helperText={shown.shape ?? 'Chosen once'}
          >
            {shapes.map((s) => <MenuItem key={s.shape} value={s.shape}>{s.shape}</MenuItem>)}
          </TextField>
          <TextField
            select label="System prompt" value={draft.system_prompt_ref} size="small" sx={{ flex: '1 1 240px' }}
            onChange={(e) => set('system_prompt_ref', e.target.value)}
            error={!!shown.system_prompt_ref} helperText={shown.system_prompt_ref ?? ' '}
          >
            {shellPrompts.map((p) => <MenuItem key={p.id} value={p.name}>{p.title}</MenuItem>)}
          </TextField>
          <TextField
            select label="Model" value={draft.model_id} size="small" sx={{ flex: '2 1 300px' }}
            onChange={(e) => set('model_id', e.target.value)}
            error={!!shown.model_id} helperText={shown.model_id ?? ' '}
          >
            {modelOptions(models)}
          </TextField>
        </Box>

        {shapeInfo && (
          <Box sx={{ mt: 0.5, mb: 1, px: 1.5, py: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="caption" component="div">Acts as: {shapeInfo.principal}</Typography>
            <Typography variant="caption" component="div">Remembers: {shapeInfo.memory}</Typography>
            <Typography variant="caption" component="div">Delivers: {shapeInfo.delivery}</Typography>
            <Typography variant="caption" component="div">Invoked by: {shapeInfo.invoked_by}</Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
          <TextField
            label="Max tokens" value={draft.max_tokens} type="number" size="small" sx={{ flex: '1 1 160px' }}
            onChange={(e) => set('max_tokens', e.target.value)}
            error={!!shown.max_tokens} helperText={shown.max_tokens ?? 'Max output tokens'}
          />
          <TextField
            select label="Thinking effort" value={draft.thinking_effort} size="small" sx={{ flex: '1 1 160px' }}
            onChange={(e) => set('thinking_effort', e.target.value)}
            helperText="None for sub-agents"
          >
            <MenuItem value=""><em>None</em></MenuItem>
            {THINKING_EFFORT_TIERS.map((tier) => <MenuItem key={tier} value={tier}>{tier}</MenuItem>)}
          </TextField>
          <TextField
            label="Context window size" value={draft.context_window_size} type="number" size="small" sx={{ flex: '1 1 180px' }}
            onChange={(e) => set('context_window_size', e.target.value)}
            error={!!shown.context_window_size}
            helperText={shown.context_window_size ?? 'Tokens; an interactive agent needs one'}
          />
        </Box>

        {shapeInfo && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Tools: {draft.tools.length} selected. A warned tool saves; the wall refuses its calls where the agent runs
            </Typography>
            <Box sx={{ display: 'flex', mt: 0.5, border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
              <ToolScopePicker
                selectableActionIds={shapeInfo.tools.map((t) => t.action_id)}
                selectedActionIds={draft.tools}
                toolWarnings={toolWarningsOf(shapeInfo)}
                onToggle={(id) => toggle('tools', [id], !draft.tools.includes(id))}
                onToggleMany={(ids, select) => toggle('tools', ids, select)}
              />
            </Box>
          </Box>
        )}

        {takesCallees && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" component="div">
              Specialists it may consult
            </Typography>
            {create.specialistsError && (
              <InlineAlert severity="error">Could not read the specialists: {create.specialistsError}</InlineAlert>
            )}
            {create.specialists.length === 0 && !create.specialistsError && (
              <Typography variant="body2" color="text.secondary">No active specialist</Typography>
            )}
            {create.specialists.map((a) => (
              <FormControlLabel
                key={a.agent_id}
                control={(
                  <Checkbox
                    size="small"
                    checked={draft.callees.includes(a.agent_id)}
                    onChange={(e) => toggle('callees', [a.agent_id], e.target.checked)}
                  />
                )}
                label={<Typography variant="body2">{a.display_name} ({a.agent_id})</Typography>}
              />
            ))}
          </Box>
        )}

        {create.error && <InlineAlert severity="error" sx={{ mt: 2 }}>{create.error}</InlineAlert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={submit} variant="contained" disabled={create.creating}>Create agent</Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewAgentDialog;
